import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Eye, KeyRound, Loader2, Radio, XCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const beDate = (value) => (value ? value.slice(0, 10).split("-").reverse().join("/") : "—");
const STATUS_STYLES = {
  pending: "bg-amber-500/15 border-amber-500/30 text-amber-300",
  verified: "bg-[#3A5CC8]/15 border-blue-600/30 text-blue-300",
  active: "bg-green-600/15 border-green-600/30 text-green-400",
  rejected: "bg-red-600/15 border-red-600/30 text-red-400",
};
const field = "w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]";
const btn = "inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-200 disabled:opacity-50";

export const AdminPeppol = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ api_key: "", api_secret: "", webhook_secret: "" });
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState("");
  const [reason, setReason] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(() => {
    axios.get(`${API}/admin/peppol/settings`, { headers }).then(({ data }) => setSettings(data)).catch(() => {});
    axios.get(`${API}/admin/peppol`, { headers }).then(({ data }) => setList(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setBusy("settings");
    try {
      const { data } = await axios.put(`${API}/admin/peppol/settings`, form, { headers });
      toast[data.test?.ok ? "success" : data.configured ? "warning" : "success"](
        data.test?.ok ? "Clé enregistrée — connexion Recommand vérifiée." : data.configured ? `Clé enregistrée. ${data.test?.detail || ""}` : "Réglages enregistrés.",
      );
      setForm({ api_key: "", api_secret: "", webhook_secret: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible."); }
    finally { setBusy(""); }
  };

  const clearSettings = async () => {
    if (!window.confirm("Retirer la clé Recommand ? L'envoi Peppol repassera en attente d'activation.")) return;
    setBusy("settings");
    try {
      await axios.put(`${API}/admin/peppol/settings`, { clear: true }, { headers });
      toast.success("Clé retirée.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action impossible."); }
    finally { setBusy(""); }
  };

  const decide = async (email, action) => {
    setBusy(`${action}-${email}`);
    try {
      const { data } = await axios.post(`${API}/admin/peppol/${encodeURIComponent(email)}/${action}`, { reason: reason[email] || "" }, { headers });
      toast.success(action === "validate" ? (data.status === "active" ? "Société activée sur Peppol." : "Société vérifiée (Peppol en attente de la clé).") : "Dossier refusé, l'artisan est prévenu.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action impossible."); }
    finally { setBusy(""); }
  };

  const openProof = async (email) => {
    try {
      const res = await axios.get(`${API}/admin/peppol/${encodeURIComponent(email)}/proof`, { headers, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    } catch { toast.error("Preuve introuvable."); }
  };

  return (
    <div data-testid="admin-peppol" className="mt-8 space-y-6">
      <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <h2 className="font-heading text-xl font-semibold inline-flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#3A5CC8]" /> Point d'accès Peppol (Recommand)</h2>
        <p className="mt-2 text-sm text-[#93A0BC] leading-relaxed">
          Une seule clé Devisto pour toutes les sociétés. Créez-la sur app.recommand.eu → Paramètres → API, puis collez-la ici : l'envoi réel s'active sans redéploiement.
        </p>
        {settings && (
          <p data-testid="admin-peppol-configured" className={`mt-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold ${settings.configured ? STATUS_STYLES.active : STATUS_STYLES.pending}`}>
            {settings.configured ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
            {settings.configured ? `Clé active ${settings.api_key_masked}${settings.from_env ? " (variable d'environnement)" : ""}` : "En attente d'activation — aucune clé"}
          </p>
        )}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input data-testid="admin-peppol-key" type="password" className={field} placeholder="Clé API Recommand" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} />
          <input data-testid="admin-peppol-secret" type="password" className={field} placeholder="Secret API Recommand" value={form.api_secret} onChange={(e) => setForm({ ...form, api_secret: e.target.value })} />
          <input data-testid="admin-peppol-webhook" type="password" className={field} placeholder="Secret webhook (facultatif)" value={form.webhook_secret} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })} />
        </div>
        {settings && <p className="mt-2 text-xs text-[#5C6A87]">URL webhook à déclarer chez Recommand : <span className="font-mono text-[#93A0BC]">{settings.webhook_url}</span></p>}
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" data-testid="admin-peppol-save" disabled={busy === "settings"} onClick={saveSettings} className={`${btn} bg-[#3A5CC8] text-white hover:bg-[#3A5CC8]`}>
            {busy === "settings" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Radio className="w-3.5 h-3.5" />} Enregistrer et tester
          </button>
          {settings?.configured && !settings.from_env && (
            <button type="button" data-testid="admin-peppol-clear" disabled={!!busy} onClick={clearSettings} className={`${btn} border border-red-500/40 text-red-400 hover:bg-red-500/10`}>
              <XCircle className="w-3.5 h-3.5" /> Retirer la clé
            </button>
          )}
        </div>
      </section>

      <section>
        <h2 className="font-heading text-xl font-semibold">Dossiers d'activation {list ? `(${list.pending} à vérifier)` : ""}</h2>
        <div data-testid="admin-peppol-list" className="mt-4 space-y-3">
          {list && !list.items.length && <p className="text-sm text-[#5C6A87]">Aucun dossier soumis pour le moment.</p>}
          {(list?.items || []).map((d) => (
            <article key={d.owner_email} data-testid={`admin-peppol-${d.owner_email}`} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
              <div className="flex flex-wrap items-center gap-3">
                <span className={`rounded-full border px-3 py-1 text-xs font-bold ${STATUS_STYLES[d.status] || STATUS_STYLES.pending}`}>{d.status_label}</span>
                <span className="text-sm font-semibold text-white">{d.company_name || d.owner_email}</span>
                <span className="text-xs text-[#93A0BC]">{d.vat} · {d.peppol_address}</span>
                <span className="text-xs text-[#5C6A87]">soumis le {beDate(d.submitted_at)}</span>
              </div>
              <p className="mt-2 text-xs text-[#93A0BC]">{d.owner_email} · {d.address || "adresse VIES indisponible"} · {d.proof_kind_label}{d.proof_name ? ` (${d.proof_name})` : ""}</p>
              {d.rejected_reason && <p className="mt-1 text-xs text-red-400">Refus : {d.rejected_reason}</p>}
              {d.recommand_company_id && <p className="mt-1 text-xs text-green-400">Recommand : {d.recommand_company_id}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button type="button" data-testid={`admin-peppol-proof-${d.owner_email}`} onClick={() => openProof(d.owner_email)} className={`${btn} border border-slate-700 text-[#93A0BC] hover:border-slate-500`}>
                  <Eye className="w-3.5 h-3.5" /> Voir la preuve
                </button>
                {d.status !== "active" && (
                  <button type="button" data-testid={`admin-peppol-validate-${d.owner_email}`} disabled={!!busy} onClick={() => decide(d.owner_email, "validate")} className={`${btn} bg-green-600 text-white hover:bg-green-500`}>
                    {busy === `validate-${d.owner_email}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Valider
                  </button>
                )}
                {d.status === "pending" && (
                  <>
                    <input data-testid={`admin-peppol-reason-${d.owner_email}`} className={`${field} sm:w-72`} placeholder="Motif du refus" value={reason[d.owner_email] || ""} onChange={(e) => setReason({ ...reason, [d.owner_email]: e.target.value })} />
                    <button type="button" data-testid={`admin-peppol-reject-${d.owner_email}`} disabled={!!busy} onClick={() => decide(d.owner_email, "reject")} className={`${btn} border border-red-500/40 text-red-400 hover:bg-red-500/10`}>
                      <XCircle className="w-3.5 h-3.5" /> Refuser
                    </button>
                  </>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};
